import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum WorkflowTypes {
  Plaid = 'Plaid',
  ShiftImpact = 'ShiftImpact',
  Conversation = 'Conversation',
}

export enum ShiftImpactSource {
  Dashboard = 'dashboard',
  ShiftDetails = 'shiftDetails',
}

type WorkflowSuccessCallback = () => void | Promise<void>;

const plaidSuccessCallbacks = new Map<number, WorkflowSuccessCallback>();

export const registerPlaidSuccessCallback = (sourceActionId: number, callback: WorkflowSuccessCallback) => {
  plaidSuccessCallbacks.set(sourceActionId, callback);
};

export const consumePlaidSuccessCallback = (sourceActionId: number): WorkflowSuccessCallback | undefined => {
  const callback = plaidSuccessCallbacks.get(sourceActionId);
  if (callback) plaidSuccessCallbacks.delete(sourceActionId);
  return callback;
};

interface ActiveWorkflow {
  success: boolean;
  type: WorkflowTypes;
  isLoading?: boolean;
  successMessage?: string;
  fullScreen?: boolean;
  sourceActionId?: number;
  openPlaid?: boolean;
  shiftId?: number;
  shiftRequirementId?: number;
  shiftImpactSource?: ShiftImpactSource;
}

export interface AIChatState {
  isOpen: boolean;
  headerTitle?: string;
  activeWorkflow?: ActiveWorkflow;
  conversationCount: number;
}

const initialState: AIChatState = {
  isOpen: false,
  conversationCount: 0,
};

const aiChat = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    openAIChat: (state: AIChatState) => {
      state.isOpen = true;
    },
    closeAIChat: (state: AIChatState) => {
      state.isOpen = false;
      state.activeWorkflow = undefined;
      state.conversationCount = 0;
    },
    toggleAIChat: (state: AIChatState) => {
      state.isOpen = !state.isOpen;
      if (!state.isOpen) {
        state.conversationCount = 0;
      }
    },
    incrementConversationCount: (state: AIChatState) => {
      state.conversationCount += 1;
    },
    setActiveWorkflow: (state: AIChatState, action: PayloadAction<ActiveWorkflow | undefined>) => {
      state.activeWorkflow = action.payload;

      if (action.payload?.fullScreen) {
        state.headerTitle = 'Select account';
      }

      if (!action.payload?.fullScreen && state.headerTitle) {
        state.headerTitle = undefined;
      }
    },
    clearActiveWorkflow: (state: AIChatState) => {
      state.activeWorkflow = undefined;
      if (state.headerTitle) {
        state.headerTitle = undefined;
      }
    },
    setHeaderTitle: (state: AIChatState, action: PayloadAction<string>) => {
      state.headerTitle = action.payload;
    },
  },
});

export const {
  openAIChat,
  closeAIChat,
  toggleAIChat,
  incrementConversationCount,
  setActiveWorkflow,
  clearActiveWorkflow,
  setHeaderTitle,
} = aiChat.actions;

export default aiChat.reducer;
